/**
 * Detect whether a TE_SEQUENCES exercise still has unplayed sequence blocks
 * relative to the live coach view (currentSequence is 1-based).
 *
 * During the competency manual gap, the server often reports currentSequence as
 * the *upcoming* block — treat that as pending.
 */
export function getFinalizePendingSequenceInfo({
  sequences,
  currentSequence,
  awaitingManualSequenceStart,
} = {}) {
  const list = Array.isArray(sequences) ? sequences : [];
  const total = list.length;
  if (total <= 1) {
    return { hasPending: false, total, pending: null, current: null };
  }

  const curNum = Number(currentSequence);
  const current =
    Number.isFinite(curNum) && curNum > 0 ? Math.floor(curNum) : 1;

  if (awaitingManualSequenceStart) {
    const pending = Math.min(Math.max(current, 1), total);
    return { hasPending: true, total, pending, current };
  }

  if (current < total) {
    return { hasPending: true, total, pending: current + 1, current };
  }

  return { hasPending: false, total, pending: null, current };
}

/**
 * Confirm before coach finalize. Uses window.confirm to match other planning/
 * destructive confirms in this app (e.g. delete sequence).
 *
 * @returns {boolean} true if the coach confirmed finalize
 */
export function confirmFinalizeExercise({
  sequences,
  currentSequence,
  awaitingManualSequenceStart,
  t,
} = {}) {
  const info = getFinalizePendingSequenceInfo({
    sequences,
    currentSequence,
    awaitingManualSequenceStart,
  });

  const translate = typeof t === "function" ? t : null;

  let message;
  if (info.hasPending) {
    const interpolated = translate
      ? translate("confirmFinalizeExercisePendingSequences", {
          pending: info.pending,
          total: info.total,
        })
      : null;
    message =
      interpolated &&
      interpolated !== "confirmFinalizeExercisePendingSequences"
        ? interpolated
        : `Sequence ${info.pending} of ${info.total} is still pending. Finalize anyway?`;
  } else {
    const base = translate
      ? translate("confirmFinalizeExercise")
      : null;
    message =
      (base && base !== "confirmFinalizeExercise" ? base : null) ||
      "Are you sure you want to finalize this exercise?";
  }

  return typeof window.confirm === "function" ? window.confirm(message) : false;
}
